import { useMutation } from '@tanstack/react-query';
import { Order, VehicleTypes, OrderDestination, type Currency } from '@hero/schema';
import { buildReorderCreatePayload } from './reorder/buildReorderCreatePayload';
import { buildReorderPriceCalcRequest } from './reorder/buildReorderPriceCalcRequest';

/**
 * Data structure for order again functionality
 * This is the single source of truth for the order again data structure
 */
export interface OrderAgainData {
  order: Order;
  vehicleType: VehicleTypes;
  vehicleCount: number;
  dateRange: { from: Date; to: Date };
}

export interface UseOrderAgainConfig {
  /**
   * Viewer org id for party-scoped price calculation. Typically the
   * signed-in user's org from `useCurrentUser().orgId`.
   */
  orgId?: string;

  /**
   * The function to calculate multi-route pricing
   * Should be the SDK's calcMultiRoutesPrice method
   */
  calculateMultiPrice: (payload: any) => Promise<any>;

  /**
   * The function to create a multi-order
   * Should be the SDK's createMultiOrder method
   */
  createMultiOrder: (payload: any) => Promise<any>;

  /**
   * Success callback when price is calculated
   */
  onPriceCalculated?: (priceData: any) => void;

  /**
   * Success callback when order is created
   * Receives the array of created orders
   */
  onSuccess?: (createdOrders: Order[]) => void;

  /**
   * Error callback when order creation or price calculation fails
   */
  onError?: (error: Error) => void;
}

export interface OrderAgainOptions {
  /**
   * Override vehicle type (optional)
   * @deprecated prefer `vehicleTypes` for multi-vehicle lanes
   */
  vehicleType?: VehicleTypes;

  /**
   * Override accepted vehicle types — the lane can move with any of
   * these. The first entry is also used for the price calc API
   * (`CalculatePriceMultiRoutesAPI` only takes a single `vehicle`).
   * When omitted, falls back to `vehicleType`, then to the original
   * order's `load.vehicles[]`.
   */
  vehicleTypes?: VehicleTypes[];

  /**
   * Override vehicle count (optional)
   */
  vehicleCount?: number;

  /**
   * Override pickup date range (optional)
   */
  dateRange?: { from: Date; to: Date };

  /**
   * Optional **one-time price lock**. When set, the new order is created
   * with `route.price` so the backend pricing resolver is bypassed for
   * this leg and the persisted strategy is `one_time_pricing` (see
   * `CreateOrderAPI['req']['price']` and `getPriceCategory` in
   * `@hero/core`).
   *
   * Caller decides whether to lock based on the **source order's** viewer
   * leg final state ({@link getPriceFinality} from `@hero/core`):
   *
   *   - `manual` with load price > 0 → forward (off-route / manually set
   *     price — includes unresolved fill-in and route drift).
   *   - `route_quoted`              → omit; let the resolver re-pick from
   *     the current Route book (agreement or public rate may have changed).
   *   - `manual` with load price 0 → omit; let the resolver try again.
   *
   * Currency defaults to the source order's currency on the consumer
   * side; `taxable` defaults to whatever the source order's leg uses.
   */
  oneTimePrice?: {
    itemsOriginalSubtotal: number;
    currency?: Currency;
    taxable?: boolean;
  };
}

export interface UseOrderAgainReturn {
  /**
   * Function to create a new order from an existing order
   * This will first calculate the price, then create the order
   *
   * @param order - The original order to duplicate
   * @param options - Optional overrides for vehicle type, count, and pickup date
   */
  orderAgain: (order: Order, options?: OrderAgainOptions) => void;

  /**
   * Whether the price is being calculated
   */
  isCalculatingPrice: boolean;

  /**
   * Whether the order is being created
   */
  isPublishing: boolean;

  /**
   * Whether any operation is in progress (calculating or publishing)
   */
  isLoading: boolean;
}

/**
 * Hook for creating a new order based on an existing order (Order Again functionality)
 *
 * This hook:
 * 1. Calculates the price for the order
 * 2. Creates the order with the calculated price
 *
 * Used in both customer dashboard and customer mobile app.
 *
 * @example
 * ```tsx
 * const { orderAgain, isLoading } = useOrderAgain({
 *   calculateMultiPrice: customerAPI.price.calcMultiRoutesPrice,
 *   createMultiOrder: customerAPI.orders.createMultiOrder,
 *   onPriceCalculated: (priceData) => console.log('Price:', priceData),
 *   onSuccess: () => toast.success('Order created successfully'),
 *   onError: (error) => toast.error(`Failed: ${error.message}`),
 * });
 *
 * // Use it
 * <Button onClick={() => orderAgain(existingOrder)} disabled={isLoading}>
 *   Order Again
 * </Button>
 * ```
 */
export const useOrderAgain = (config: UseOrderAgainConfig): UseOrderAgainReturn => {
  const { calculateMultiPrice, createMultiOrder, onPriceCalculated, onSuccess, onError, orgId } =
    config;

  // Mutation for calculating price
  const {
    mutate: calculatePrice,
    isPending: isCalculatingPrice,
    data: priceData,
  } = useMutation({
    mutationFn: calculateMultiPrice,
    onSuccess: data => {
      onPriceCalculated?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });

  // Mutation for creating order
  const { mutate: publishOrder, isPending: isPublishing } = useMutation({
    mutationFn: createMultiOrder,
    onSuccess: (createdOrders: Order[]) => {
      onSuccess?.(createdOrders);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });

  // Type guard to check if an Order has valid destinations
  const hasDestinations = (obj: Order | undefined) => {
    if (!obj) {
      return false;
    }
    if (!obj.destinations) {
      return false;
    }
    return (
      Array.isArray(obj.destinations) &&
      obj.destinations.length >= 2 &&
      obj.destinations.every(
        (dest): dest is OrderDestination =>
          typeof dest === 'object' &&
          dest !== null &&
          'type' in dest &&
          'address' in dest,
      )
    );
  };

  // Helper type for orders that might have nested structures
  type OrderWithNested = Order & {
    order?: Order;
    doc?: Order;
  };

  // Helper to extract order data from various structures
  const extractOrderData = (order: Order): Order => {
    // Check if order has destinations directly
    if (hasDestinations(order)) {
      return order;
    }

    // Check for nested structures - safely access optional properties
    const orderWithNested = order as OrderWithNested;

    if (orderWithNested.order && hasDestinations(orderWithNested.order)) {
      return orderWithNested.order;
    }

    if (orderWithNested.doc && hasDestinations(orderWithNested.doc)) {
      return orderWithNested.doc;
    }

    // Fallback to original order (will be validated later)
    return order;
  };

  const handleOrderAgain = (order: Order, options?: OrderAgainOptions) => {
    // Extract order data from various possible structures
    const orderData = extractOrderData(order);

    // Resolve the accepted-vehicles array first so single-type callers
    // and multi-type callers funnel through the same code path. Falls
    // back to the original order's `load.vehicles[]` (multi-type
    // lanes) and finally to a sane default truck.
    const resolvedVehicleTypes: VehicleTypes[] = (() => {
      if (options?.vehicleTypes && options.vehicleTypes.length > 0) {
        return options.vehicleTypes;
      }
      if (options?.vehicleType) return [options.vehicleType];
      if (Array.isArray(orderData.load?.vehicles) && orderData.load.vehicles.length > 0) {
        return orderData.load.vehicles as VehicleTypes[];
      }
      return ['dyna_closed' as VehicleTypes];
    })();

    // Price calc API is single-vehicle — use the first selected type.
    const vehicleType = resolvedVehicleTypes[0];
    // Default one truck on reorder; only use an explicit override from
    // the modal (or other caller), not the source order's quantity.
    const vehicleCount = options?.vehicleCount ?? 1;
    const pickupDateRange = options?.dateRange || {
      from: orderData.dates?.pickup?.from || new Date(),
      to: orderData.dates?.pickup?.to || new Date(),
    };

    if (!orderData.destinations || orderData.destinations.length < 2) {
      const errorMsg = `Invalid order: Order has ${
        orderData.destinations?.length || 0
      } destinations, expected at least 2`;
      console.error(errorMsg, { order, orderData });
      onError?.(new Error(errorMsg));
      return;
    }

    const priceCalculatePayload = buildReorderPriceCalcRequest(orderData, orgId, {
      vehicleType,
      vehicleCount,
    });

    const buildCreatePayload = () =>
      buildReorderCreatePayload(orderData, {
        vehicleTypes: resolvedVehicleTypes,
        vehicleCount,
        dateRange: pickupDateRange,
        oneTimePrice: options?.oneTimePrice,
      });

    const publish = () => publishOrder(buildCreatePayload());

    if (!priceCalculatePayload) {
      publish();
      return;
    }

    // Calc errors are non-fatal — always submit the create payload.
    calculatePrice(priceCalculatePayload, {
      onSuccess: publish,
      onError: publish,
    });
  };

  return {
    orderAgain: handleOrderAgain,
    isCalculatingPrice,
    isPublishing,
    isLoading: isCalculatingPrice || isPublishing,
  };
};
